import { useMemo, useState, type ReactNode } from 'react';
import type { AppState } from '../../shared/api';
import { fitsWanted } from '../../shared/catalog';
import { countryName } from '../../shared/countries';
import { useUpdateProfile } from '../api';
import { Sheet } from '../components/Sheet';
import { VariantPicker } from '../components/VariantPicker';
import { sizeLabel } from '../format';
import { promptInstall, useInstallState } from '../pwa';
import { ShareSheet } from './Wanted';

interface Props {
  state: AppState;
  onDatabase: () => void;
  onPreview: () => void;
  onLogout: () => void;
  onToast: (message: string) => void;
}

/** Everything that is not a list of cans: the numbers, the database, settings and the account. */
export function Overig({ state, onDatabase, onPreview, onLogout, onToast }: Props) {
  const { profile } = state;
  const install = useInstallState();
  const [sheet, setSheet] = useState<'defaults' | 'share' | null>(null);
  const [installHelp, setInstallHelp] = useState(false);

  const stats = useMemo(() => {
    const perCountry = new Map<string, number>();
    for (const e of state.entries) perCountry.set(e.country, (perCountry.get(e.country) ?? 0) + 1);
    return {
      flavors: new Set(state.entries.map((e) => e.canId)).size,
      countries: [...perCountry].sort((a, b) => b[1] - a[1]),
    };
  }, [state.entries]);

  const sharedCount = state.wanted.filter((w) => !state.entries.some((e) => fitsWanted(w, e))).length;

  const installRow = () => {
    if (install === 'prompt') {
      return (
        <Row
          name="Zet op je beginscherm"
          sub="Opent dan als app, zonder adresbalk."
          onClick={async () => {
            if (await promptInstall()) onToast('Monsters staat op je beginscherm.');
          }}
        />
      );
    }
    return (
      <>
        <Row
          name="Zet op je beginscherm"
          sub="Opent dan als app, zonder adresbalk."
          expanded={installHelp}
          onClick={() => setInstallHelp((open) => !open)}
        />
        {installHelp && (
          <div className="set-help">
            {install === 'ios'
              ? 'Tik in Safari op Deel en kies "Zet op beginscherm".'
              : 'Open het menu van je browser en kies "Installeren" of "Toevoegen aan startscherm".'}
          </div>
        )}
      </>
    );
  };

  return (
    <main className="screen screen--padded">
      <div className="overig">
        <header>
          <h1 className="title">OVERIG</h1>
          <div className="lede">
            {profile.displayName} · {location.host}/{profile.shareSlug}
          </div>
        </header>

        <div className="overig__grid">
          <section aria-labelledby="overig-collectie">
            <h2 className="label" id="overig-collectie">
              Je collectie
            </h2>
            <div className="set-list">
              <dl className="stats">
                <div>
                  <dd>{state.entries.length}</dd>
                  <dt>{state.entries.length === 1 ? 'blik' : 'blikken'}</dt>
                </div>
                <div>
                  <dd>
                    {stats.flavors}
                    <small>/{state.cans.length}</small>
                  </dd>
                  <dt>soorten</dt>
                </div>
                <div>
                  <dd>{stats.countries.length}</dd>
                  <dt>{stats.countries.length === 1 ? 'land' : 'landen'}</dt>
                </div>
              </dl>
              {stats.countries.length > 0 && (
                <div className="set-help">
                  Meeste uit{' '}
                  {stats.countries
                    .slice(0, 3)
                    .map(([code, n]) => `${countryName(code)} ${n}`)
                    .join(' · ')}
                </div>
              )}
              <Row name="Database" sub={`Alle ${state.cans.length} soorten en hun barcodes`} onClick={onDatabase} />
            </div>
          </section>

          <section aria-labelledby="overig-toevoegen">
            <h2 className="label" id="overig-toevoegen">
              Toevoegen
            </h2>
            <div className="set-list">
              <Row
                name="Land en inhoud"
                sub="Staat al ingevuld bij een nieuw blik."
                value={`${countryName(profile.defaultCountry)} · ${sizeLabel(profile.defaultSizeMl)}`}
                onClick={() => setSheet('defaults')}
              />
            </div>
          </section>

          <section aria-labelledby="overig-delen">
            <h2 className="label" id="overig-delen">
              Delen
            </h2>
            <div className="set-list">
              <Row
                name="Zoeklijst delen"
                sub={profile.allowReservations ? 'Vrienden mogen reserveren.' : 'Vrienden kunnen alleen meekijken.'}
                onClick={() => setSheet('share')}
              />
            </div>
          </section>

          {install !== 'installed' && (
            <section aria-labelledby="overig-app">
              <h2 className="label" id="overig-app">
                App
              </h2>
              <div className="set-list">{installRow()}</div>
            </section>
          )}

          <section aria-labelledby="overig-account">
            <h2 className="label" id="overig-account">
              Account
            </h2>
            <div className="set-list">
              <Row name="Uitloggen" plain onClick={onLogout} />
            </div>
          </section>
        </div>

        <p className="credit overig__credit">
          Soorten en blikfoto's: <a href="https://beastvault.app" target="_blank" rel="noreferrer">BeastVault</a>.
          Barcodes: <a href="https://openfoodfacts.org" target="_blank" rel="noreferrer">Open Food Facts</a> (ODbL) en
          je eigen scans.
        </p>
      </div>

      {sheet === 'defaults' && <DefaultsSheet state={state} onClose={() => setSheet(null)} />}
      {sheet === 'share' && (
        <ShareSheet state={state} count={sharedCount} onPreview={onPreview} onClose={() => setSheet(null)} />
      )}
    </main>
  );
}

interface RowProps {
  name: string;
  sub?: ReactNode;
  value?: string;
  /** No chevron: the row does something right here instead of opening something. */
  plain?: boolean;
  expanded?: boolean;
  onClick: () => void;
}

function Row({ name, sub, value, plain, expanded, onClick }: RowProps) {
  return (
    <button type="button" className="set-row" aria-expanded={expanded} onClick={onClick}>
      <span className="set-row__body">
        <span className="set-row__name">{name}</span>
        {sub && <span className="set-row__sub">{sub}</span>}
      </span>
      {value && <span className="set-row__value">{value}</span>}
      {!plain && (
        <span className="set-row__chevron" aria-hidden="true">
          ›
        </span>
      )}
    </button>
  );
}

/** The country and size the add sheet starts on; saved as you pick. */
function DefaultsSheet({ state, onClose }: { state: AppState; onClose: () => void }) {
  const { profile } = state;
  const updateProfile = useUpdateProfile();

  return (
    <Sheet label="Standaard land en inhoud" onClose={onClose}>
      {(close) => (
        <>
          <h2 className="sheet__name">Standaard bij toevoegen</h2>
          <div className="card__text">
            Dit staat al ingevuld als je een blik toevoegt. Per blik kun je het nog aanpassen, en een gescande barcode
            die land en inhoud kent gaat voor.
          </div>
          <VariantPicker
            country={profile.defaultCountry}
            sizeMl={profile.defaultSizeMl}
            onCountry={(defaultCountry) => updateProfile.mutate({ defaultCountry })}
            onSize={(defaultSizeMl) => updateProfile.mutate({ defaultSizeMl })}
          />
          <button type="button" className="btn" style={{ marginTop: 24 }} onClick={close}>
            Klaar
          </button>
        </>
      )}
    </Sheet>
  );
}
